import { createSignal, type JSX, onCleanup, onMount } from "solid-js";

interface ScrollableFadeContainerProps {
  children: JSX.Element;
  maxHeight?: string;
  autoScroll?: boolean;
  /** How far down the top fade extends. Default "24px". */
  topFadeDepth?: string;
  /** Starting alpha (0–1) at the very top of the fade. Default 0 (fully transparent). */
  topFadeStartOpacity?: number;
}

export const ScrollableFadeContainer = (props: ScrollableFadeContainerProps) => {
  let containerRef: HTMLDivElement | undefined;
  let contentRef: HTMLDivElement | undefined;
  let userScrolledUp = false;
  let lastScrollTop = 0;

  const [showTopFade, setShowTopFade] = createSignal(false);
  const [showBottomFade, setShowBottomFade] = createSignal(false);

  const checkScroll = () => {
    if (!containerRef) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef;
    const hasOverflow = scrollHeight > clientHeight + 5;
    setShowTopFade(hasOverflow && scrollTop > 10);
    setShowBottomFade(hasOverflow && scrollTop + clientHeight < scrollHeight - 10);
  };

  const handleScroll = () => {
    if (!containerRef) return;
    const currentScrollTop = containerRef.scrollTop;
    const { scrollHeight, clientHeight } = containerRef;
    const distanceFromBottom = scrollHeight - clientHeight - currentScrollTop;

    if (currentScrollTop < lastScrollTop - 5) {
      userScrolledUp = true;
    }

    if (distanceFromBottom < 20) {
      userScrolledUp = false;
    }

    lastScrollTop = currentScrollTop;
    checkScroll();
  };

  const scrollToBottom = () => {
    if (!containerRef || !props.autoScroll || userScrolledUp) return;
    containerRef.scrollTop = containerRef.scrollHeight;
  };

  onMount(() => {
    checkScroll();

    let resizeRafId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
      resizeRafId = requestAnimationFrame(() => {
        checkScroll();
        scrollToBottom();
        resizeRafId = null;
      });
    });

    if (contentRef) {
      resizeObserver.observe(contentRef);
    }

    const mutationObserver = new MutationObserver(() => {
      checkScroll();
      scrollToBottom();
    });

    if (contentRef) {
      // Only observe direct child additions/removals, not text changes
      // Text content changes during streaming are handled by ResizeObserver
      mutationObserver.observe(contentRef, {
        childList: true,
      });
    }

    onCleanup(() => {
      if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    });
  });

  const maskStyle = () => {
    const top = showTopFade();
    const bottom = showBottomFade();

    if (!top && !bottom) return {};

    const topDepth = props.topFadeDepth ?? "24px";
    const topStart = props.topFadeStartOpacity ?? 0;
    const topStartColor = topStart === 0 ? "transparent" : `rgba(0,0,0,${topStart})`;

    let gradient: string;
    if (top && bottom) {
      gradient = `linear-gradient(to bottom, ${topStartColor}, black ${topDepth}, black calc(100% - 24px), transparent)`;
    } else if (top) {
      gradient = `linear-gradient(to bottom, ${topStartColor}, black ${topDepth})`;
    } else {
      gradient = "linear-gradient(to bottom, black calc(100% - 24px), transparent)";
    }

    return {
      "mask-image": gradient,
      "-webkit-mask-image": gradient,
    };
  };

  return (
    <div
      ref={containerRef}
      class="scrollbar-hide overflow-y-auto"
      style={{ "max-height": props.maxHeight || "250px", ...maskStyle() }}
      onScroll={handleScroll}
    >
      <div ref={contentRef}>{props.children}</div>
    </div>
  );
};
